import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";

// Custom hand-drawn SVG marks — one per value.
const VALUE_MARKS = [
    {
        // Community-first — three interlocking people
        accent: "from-[#2D5A27] to-[#4A7C59]",
        badgeTint: "bg-[#2D5A27]/10",
        svg: (
            <svg viewBox="0 0 64 64" fill="none" className="w-8 h-8 text-white">
                <circle cx="20" cy="24" r="7" stroke="currentColor" strokeWidth="2.5" />
                <circle cx="44" cy="24" r="7" stroke="currentColor" strokeWidth="2.5" />
                <circle cx="32" cy="42" r="7" stroke="currentColor" strokeWidth="2.5" />
                <path d="M9 56c1-6 5-10 11-10M55 56c-1-6-5-10-11-10M21 56c1-6 5-10 11-10M43 56c-1-6-5-10-11-10" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
            </svg>
        ),
    },
    {
        // Transparency — an open eye with rays
        accent: "from-[#0F766E] to-[#14B8A6]",
        badgeTint: "bg-[#0F766E]/10",
        svg: (
            <svg viewBox="0 0 64 64" fill="none" className="w-8 h-8 text-white">
                <path d="M6 34c8-12 16-18 26-18s18 6 26 18c-8 12-16 18-26 18S14 46 6 34Z" stroke="currentColor" strokeWidth="2.5" strokeLinejoin="round" />
                <circle cx="32" cy="34" r="6" fill="currentColor" />
                <path d="M14 12l4 4M50 12l-4 4M32 8v6" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
            </svg>
        ),
    },
    {
        // Dignity — rising lotus / sun
        accent: "from-[#B45309] to-[#E8A365]",
        badgeTint: "bg-[#B45309]/10",
        svg: (
            <svg viewBox="0 0 64 64" fill="none" className="w-8 h-8 text-white">
                <path d="M32 50c-8 0-14-4-16-10 4-2 10-3 16-3s12 1 16 3c-2 6-8 10-16 10Z" stroke="currentColor" strokeWidth="2.5" strokeLinejoin="round" />
                <path d="M32 37c-6-4-10-10-10-16 0 0 4 3 10 6 6-3 10-6 10-6 0 6-4 12-10 16Z" stroke="currentColor" strokeWidth="2.5" strokeLinejoin="round" />
                <path d="M20 20L14 14M44 20l6-6M12 32H6M52 32h6" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
            </svg>
        ),
    },
    {
        // Long-term — a growing tree with roots
        accent: "from-[#7C2D12] to-[#B45309]",
        badgeTint: "bg-[#7C2D12]/10",
        svg: (
            <svg viewBox="0 0 64 64" fill="none" className="w-8 h-8 text-white">
                <circle cx="32" cy="20" r="10" stroke="currentColor" strokeWidth="2.5" />
                <circle cx="21" cy="30" r="7" stroke="currentColor" strokeWidth="2.5" />
                <circle cx="43" cy="30" r="7" stroke="currentColor" strokeWidth="2.5" />
                <path d="M32 30v22M32 46l-8 6M32 46l8 6M32 40l-6 4M32 40l6 4" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
        ),
    },
];

export default function About() {
    const [content, setContent] = useState({});
    const [team, setTeam] = useState([]);

    useEffect(() => {
        api.get("/site-content").then((r) => setContent(r.data || {}));
        api.get("/team").then((r) => setTeam(r.data || []));
    }, []);

    return (
        <div data-testid="about-page">
            {/* Hero */}
            <section className="pt-20 pb-16 md:pt-32 md:pb-24">
                <div className="container-hero grid md:grid-cols-12 gap-10 items-end">
                    <div className="md:col-span-7">
                        <div className="label-eyebrow text-brand-forest mb-4">About Kabir Foundation</div>
                        <h1 className="font-display text-5xl sm:text-6xl font-semibold text-brand-ink leading-[1.05]">
                            A community of people building a fairer Bundelkhand.
                        </h1>
                    </div>
                    <div className="md:col-span-5 text-slate-600 text-lg leading-relaxed">
                        Since 2013, Kabir Foundation has worked with under-served families in and around Khajuraho — where opportunity is thin but potential is abundant. We build learning centres, livelihoods and dignity, one village at a time.
                    </div>
                </div>
            </section>

            {/* Vision / Mission */}
            <section className="py-16 md:py-24 bg-brand-stone">
                <div className="container-hero grid md:grid-cols-2 gap-8">
                    <div className="bg-white rounded-2xl p-10">
                        <div className="label-eyebrow text-brand-forest mb-3">Our Vision</div>
                        <p className="font-display text-3xl sm:text-4xl font-medium leading-tight text-brand-ink">{content.about_vision}</p>
                    </div>
                    <div className="bg-brand-forest text-white rounded-2xl p-10">
                        <div className="label-eyebrow text-brand-ochre mb-3">Our Mission</div>
                        <p className="font-display text-3xl sm:text-4xl font-medium leading-tight">{content.about_mission}</p>
                    </div>
                </div>
            </section>

            {/* Values */}
            <section className="py-24 md:py-32">
                <div className="container-hero">
                    <div className="max-w-2xl mb-12">
                        <div className="label-eyebrow text-brand-forest mb-3">Core Values</div>
                        <h2 className="font-display text-4xl sm:text-5xl font-medium">What we hold to.</h2>
                    </div>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                        {(content.about_values || []).map((v, i) => {
                            const mark = VALUE_MARKS[i % VALUE_MARKS.length];
                            return (
                                <div
                                    key={v.title}
                                    data-testid={`value-${i}`}
                                    className="group relative bg-white rounded-2xl p-8 border border-black/5 hover:border-brand-forest/30 hover:-translate-y-1 transition-transform duration-300 overflow-hidden"
                                >
                                    {/* Big number watermark */}
                                    <div className="pointer-events-none absolute -top-4 right-3 font-display text-[6rem] leading-none font-semibold text-black/5 select-none">
                                        {String(i + 1).padStart(2, "0")}
                                    </div>
                                    {/* Colored SVG mark on gradient tile */}
                                    <div
                                        className={`relative w-16 h-16 rounded-2xl bg-gradient-to-br ${mark.accent} shadow-sm grid place-items-center mb-6 group-hover:scale-[1.05] transition-transform duration-300`}
                                    >
                                        {mark.svg}
                                        <div className={`absolute -inset-2 rounded-2xl ${mark.badgeTint} -z-10 blur-lg opacity-70`} />
                                    </div>
                                    <div className="font-display text-2xl font-medium mb-2 relative">
                                        {v.title}
                                    </div>
                                    <p className="text-slate-600 text-sm leading-relaxed relative">
                                        {v.text}
                                    </p>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* Team */}
            <section className="py-24 md:py-32 bg-brand-stone" data-testid="team-section">
                <div className="container-hero">
                    <div className="max-w-2xl mb-14">
                        <div className="label-eyebrow text-brand-forest mb-3">Leadership & Team</div>
                        <h2 className="font-display text-4xl sm:text-5xl font-medium">The people behind the mission.</h2>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
                        {team.map((m, i) => (
                            <div
                                key={m.id}
                                data-testid={`team-card-${m.id}`}
                                className="group bg-white rounded-3xl overflow-hidden border border-black/5 hover:shadow-lg transition-shadow duration-300"
                            >
                                <div className="grid grid-cols-1 sm:grid-cols-5">
                                    <div className="sm:col-span-2 aspect-square sm:aspect-auto sm:min-h-full overflow-hidden bg-brand-stone">
                                        {m.image && (
                                            <img
                                                src={m.image}
                                                alt={m.name}
                                                className="w-full h-full object-cover object-top group-hover:scale-[1.04] transition-transform duration-700"
                                            />
                                        )}
                                    </div>
                                    <div className="sm:col-span-3 p-7 flex flex-col justify-center">
                                        <div className="text-[10px] uppercase tracking-[0.2em] text-brand-forest font-semibold">
                                            {i === 0 ? "President" : "Head of Division"}
                                        </div>
                                        <div className="font-display text-2xl md:text-3xl font-medium leading-tight mt-2">
                                            {m.name}
                                        </div>
                                        <div className="text-slate-500 text-sm mt-1">{m.role}</div>
                                        <div className="mt-4 h-px w-10 bg-brand-forest/40" />
                                        <p className="text-slate-600 text-sm leading-relaxed mt-4">{m.bio}</p>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>
        </div>
    );
}
